import React from 'react';
import { useAppStore } from '../store/useAppStore';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
const OPT = isMac ? 'Opt' : 'Alt';

type ToolMode = 'draw' | 'fill' | 'erase' | 'stamp' | 'text' | 'blur';

interface HudEntry { keys: string; action: string; }
interface HudSections { tool: HudEntry[]; global: HudEntry[]; }

// Controls that apply regardless of tool
const GLOBAL_WITH_TRANSLATE: HudEntry[] = [
  { keys: 'X + drag',              action: 'Layer opacity' },
  { keys: 'Shift + drag',          action: 'Rotate layer' },
  { keys: `Shift + ${OPT} + drag`, action: 'Translate layer' },
  { keys: 'Bksp',                   action: 'Delete layer' },
  { keys: `${OPT} + Bksp`,          action: 'Clear layer' },
];
const GLOBAL_NO_TRANSLATE: HudEntry[] = [
  { keys: 'X + drag',              action: 'Layer opacity' },
  { keys: 'Shift + drag',          action: 'Rotate layer' },
  { keys: 'Bksp',                   action: 'Delete layer' },
  { keys: `${OPT} + Bksp`,          action: 'Clear layer' },
];

const HUD_SECTIONS: Record<ToolMode, HudSections> = {
  draw: {
    tool: [
      { keys: '[ / ]',   action: 'Brush size' },
      { keys: 'B + drag', action: 'Brush size' },
      { keys: 'V + drag', action: 'Brush opacity' },
    ],
    global: GLOBAL_WITH_TRANSLATE,
  },
  erase: {
    tool: [
      { keys: '[ / ]',    action: 'Brush size' },
      { keys: 'B + drag', action: 'Brush size' },
      { keys: 'V + drag', action: 'Brush opacity' },
    ],
    global: GLOBAL_WITH_TRANSLATE,
  },
  fill: {
    tool: [
      { keys: 'Click',         action: 'Fill with color 1' },
      { keys: 'Right-click',   action: 'Fill with color 2' },
      { keys: 'Click + drag',  action: 'Gradient fill along drag axis' },
      { keys: 'B + drag',      action: 'Adjust tolerance' },
    ],
    global: GLOBAL_WITH_TRANSLATE,
  },
  stamp: {
    tool: [
      { keys: 'Click',    action: 'Project stamp' },
      { keys: 'B + drag', action: 'Stamp size' },
      { keys: 'C + drag', action: 'Stamp rotation' },
      { keys: 'V + drag', action: 'Stamp opacity' },
    ],
    global: GLOBAL_NO_TRANSLATE,
  },
  text: {
    tool: [
      { keys: 'Click',    action: 'Place text' },
      { keys: 'C + drag', action: 'Text rotation' },
      { keys: 'V + drag', action: 'Text opacity' },
    ],
    global: GLOBAL_NO_TRANSLATE,
  },
  blur: {
    tool: [
      { keys: '[ / ]',    action: 'Blur radius' },
      { keys: 'B + drag', action: 'Blur radius' },
      { keys: 'C + drag', action: 'Blur spread' },
    ],
    global: GLOBAL_WITH_TRANSLATE,
  },
};

const TOOL_LABELS: Record<ToolMode, string> = {
  draw:  'Draw',
  erase: 'Erase',
  fill:  'Fill',
  stamp: 'Stamp',
  text:  'Text',
  blur:  'Blur',
};

const SECTION_HEADER_STYLE: React.CSSProperties = {
  color: 'rgba(255,255,255,0.4)',
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  marginBottom: 5,
  paddingBottom: 4,
  borderBottom: '1px solid rgba(255,255,255,0.07)',
};

function EntryList({ entries }: { entries: HudEntry[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {entries.map((e, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: '0 0 auto' }}>{renderKeys(e.keys)}</div>
          <span style={{ color: 'rgba(255,255,255,0.38)', fontSize: 10, flex: 1 }}>{e.action}</span>
        </div>
      ))}
    </div>
  );
}

const KBD_STYLE: React.CSSProperties = {
  display: 'inline-block',
  padding: '1px 5px',
  borderRadius: 3,
  border: '1px solid rgba(255,255,255,0.22)',
  background: 'rgba(255,255,255,0.07)',
  color: '#ccc',
  fontSize: 10,
  lineHeight: '16px',
  fontFamily: 'monospace',
  whiteSpace: 'nowrap',
};

function renderKeys(keys: string) {
  return (
    <span style={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'nowrap' }}>
      {keys.split(' / ').map((combo, ci) => (
        <React.Fragment key={ci}>
          {ci > 0 && <span style={{ color: '#555', fontSize: 10 }}>/</span>}
          {combo.trim().split(' + ').map((part, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span style={{ color: '#555', fontSize: 10, margin: '0 1px' }}>+</span>}
              <kbd style={KBD_STYLE}>{part}</kbd>
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </span>
  );
}

export default function HotkeyHud() {
  const showHotkeyHud = useAppStore((s) => s.showHotkeyHud);
  const toolMode = useAppStore((s) => s.toolMode);

  if (!showHotkeyHud) return null;

  const { tool, global } = HUD_SECTIONS[toolMode];

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 36,
        right: 10,
        zIndex: 20,
        background: 'rgba(18, 18, 18, 0.75)',
        backdropFilter: 'blur(6px)',
        border: '1px solid rgba(255,255,255,0.09)',
        borderRadius: 6,
        padding: '7px 10px 8px',
        minWidth: 200,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {/* Tool name header */}
      <div style={SECTION_HEADER_STYLE}>{TOOL_LABELS[toolMode]}</div>
      <EntryList entries={tool} />

      {/* Global controls section */}
      <div style={{ ...SECTION_HEADER_STYLE, marginTop: 7, borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 5 }}>
        Global Controls
      </div>
      <EntryList entries={global} />

      {/* Dismiss hint */}
      <div style={{ color: 'rgba(255,255,255,0.18)', fontSize: 9, marginTop: 6, textAlign: 'right' }}>
        H to toggle
      </div>
    </div>
  );
}
